import React, { useState, useEffect } from "react";
import {
	Typography,
	Card,
	Tag,
	Space,
	Button,
	Table,
	Alert,
	Message,
	Popconfirm,
	Tooltip,
	Modal,
	Form,
	DatePicker,
} from "@arco-design/web-react";
import {
	IconEye,
	IconEyeInvisible,
	IconCopy,
	IconDelete,
	IconCalendar,
} from "@arco-design/web-react/icon";
import "./style.less";
import { getUserKeys, deleteUserKey, createUserKey } from "@/api";

const { Title, Text } = Typography;

const UserKeyManager: React.FC = () => {
	const [userKeys, setUserKeys] = useState([]);
	const [loading, setLoading] = useState(false);
	const [createModalVisible, setCreateModalVisible] = useState(false);
	const [form] = Form.useForm();
	const [visibleKeys, setVisibleKeys] = useState<Set<string>>(new Set());

	useEffect(() => {
		fetchUserKeys();
	}, []);

	// 获取用户Key数据
	const fetchUserKeys = async () => {
		setLoading(true);
		try {
			const response = await getUserKeys();
			if (response.status === 200) {
				setUserKeys(response.data as any[]);
			}
		} catch (error) {
			Message.error(error.response.data.message);
		} finally {
			setLoading(false);
		}
	};

	// 删除Key
	const handleRevokeKey = async (userKey: string, id: Array<string>) => {
		setLoading(true);
		try {
			await deleteUserKey(userKey, id);
			setUserKeys((prev) => prev.filter((key) => key.id !== id[0]));
			Message.success("删除密钥成功");
		} catch (error) {
			Message.error("删除失败，请重试");
		} finally {
			setLoading(false);
		}
	};

	// 获取状态颜色
	const getStatusColor = (key) => {
		const status = getKeyStatus(key);
		switch (status) {
			case "active":
				return "green";
			case "expired":
				return "orange";
			case "revoked":
				return "red";
			default:
				return "gray";
		}
	};

	// 获取状态文本
	const getStatusText = (key) => {
		const status = getKeyStatus(key);
		switch (status) {
			case "active":
				return "正常";
			case "expired":
				return "已过期";
			case "revoked":
				return "已撤销";
			default:
				return "未知";
		}
	};

	// 复制Key
	const handleCopyKey = (key: string) => {
		navigator.clipboard.writeText(key);
		Message.success("Key已复制到剪贴板");
	};

	// 判断Key是否过期
	const isKeyExpired = (expiresAt: string) => {
		const now = new Date();
		const expireDate = new Date(expiresAt);
		return now > expireDate;
	};

	// 获取Key的实际状态
	const getKeyStatus = (key) => {
		if (key.status === "revoked") return "revoked";
		if (isKeyExpired(key.expiresAt)) return "expired";
		return "active";
	};

	// 格式化key显示（隐藏中间部分）
	const formatKeyDisplay = (key: string, isVisible: boolean) => {
		if (isVisible) {
			return key;
		}
		if (key.length <= 8) {
			return key;
		}
		return `${key.substring(0, 4)}**************${key.substring(
			key.length - 4
		)}`;
	};

	// 切换key可见性
	const toggleKeyVisibility = (keyId: string) => {
		setVisibleKeys((prev) => {
			const newSet = new Set(prev);
			if (newSet.has(keyId)) {
				newSet.delete(keyId);
			} else {
				newSet.add(keyId);
			}
			return newSet;
		});
	};

	// 表格列定义
	const columns = [
		{
			title: "密钥",
			dataIndex: "key",
			key: "key",
			width: 250,
			render: (text: string, record) => {
				const isVisible = visibleKeys.has(record.uuid);
				return (
					<Space>
						<Text
							code
							style={{
								maxWidth: 150,
								overflow: "hidden",
								textOverflow: "ellipsis",
							}}
						>
							{formatKeyDisplay(text, isVisible)}
						</Text>
						<Tooltip content="复制密钥">
							<Button
								type="text"
								size="small"
								icon={<IconCopy />}
								onClick={() => {
									navigator.clipboard.writeText(text);
									Message.success("密钥已复制到剪贴板");
								}}
							/>
						</Tooltip>
						<Tooltip content={`${isVisible ? "隐藏" : "显示"}`}>
							<Button
								type="text"
								size="small"
								icon={isVisible ? <IconEyeInvisible /> : <IconEye />}
								onClick={() => {
									toggleKeyVisibility(record.uuid);
								}}
							/>
						</Tooltip>
					</Space>
				);
			},
		},
		{
			title: "状态",
			dataIndex: "status",
			key: "status",
			width: 100,
			render: (_, record) => (
				<Tag color={getStatusColor(record)}>{getStatusText(record)}</Tag>
			),
		},
		{
			title: "创建时间",
			dataIndex: "createdAt",
			key: "createdAt",
			width: 150,
			render: (text: string) => (
				<Space>
					<IconCalendar />
					{`${new Date(text).getFullYear()}年${
						new Date(text).getMonth() + 1
					}月${new Date(text).getDate()}日`}
				</Space>
			),
		},
		{
			title: "过期时间",
			dataIndex: "expiresAt",
			key: "expiresAt",
			width: 150,
			render: (text: string) => (
				<Space>
					<IconCalendar />
					{`${new Date(text).getFullYear()}年${
						new Date(text).getMonth() + 1
					}月${new Date(text).getDate()}日`}
				</Space>
			),
		},
		{
			title: "使用次数",
			dataIndex: "usageCount",
			key: "usageCount",
			width: 100,
			render: (count: number) => <Text>{count.toLocaleString()} </Text>,
		},
		{
			title: "最后使用",
			dataIndex: "lastUsedAt",
			key: "lastUsedAt",
			width: 150,
			render: (text: string | null) => (
				<Text>{text ? new Date(text).toLocaleString() : "从未使用"} </Text>
			),
		},
		{
			title: "操作",
			key: "actions",
			width: 120,
			render: (_, record) => (
				<Space>
					<Popconfirm
						title="确定要删除此密钥吗？"
						content="确认后该密钥将彻底删除，此操作不可恢复。"
						onOk={() => {
							handleRevokeKey(record.key, [record.uuid]);
							setTimeout(() => {
								fetchUserKeys();
							}, 500);
						}}
					>
						<Button
							type="text"
							size="small"
							status="danger"
							icon={<IconDelete />}
						>
							删除
						</Button>
					</Popconfirm>
				</Space>
			),
		},
	];

	// 打开创建密钥模态框
	const handleCreateUserKey = () => {
		setCreateModalVisible(true);
	};

	// 处理创建密钥确认
	const handleCreateKeyConfirm = async () => {
		try {
			const values = await form.validate();
			const expiresAt = values.expiresAt;

			const response = await createUserKey({ expiresAt });
			if (response.status === 200 || response.status === 201) {
				Message.success("API密钥创建成功");
				setCreateModalVisible(false);
				form.resetFields();
				fetchUserKeys(); // 刷新列表
			}
		} catch (error) {
			Message.error(error.response?.data?.message || "创建失败，请重试");
		}
	};

	// 关闭模态框
	const handleCreateKeyCancel = () => {
		setCreateModalVisible(false);
		form.resetFields();
	};

	return (
		<div className="user-key-manager">
			<div className="create_api_key_content">
				<Button
					type="primary"
					className="create_api_key_but"
					onClick={() => handleCreateUserKey()}
				>
					新建 API 密钥
				</Button>
				<Alert
					className="create_api_key_alert"
					type="warning"
					content="API密钥是您访问接口的凭证，具有该账户的完整权限，请您妥善保管。"
				/>
			</div>
			{/* Key列表 */}
			<Card className="key-table-card">
				<Table
					columns={columns}
					data={userKeys}
					loading={loading}
					pagination={{
						pageSize: 10,
						showTotal: (total, range) =>
							`第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
					}}
					rowKey="id"
				/>
			</Card>

			<Modal
				title="创建 API 密钥"
				visible={createModalVisible}
				onOk={handleCreateKeyConfirm}
				onCancel={handleCreateKeyCancel}
				okText="创建"
				cancelText="取消"
				// width={500}
			>
				<Form form={form} layout="vertical">
					<Form.Item
						label="过期时间"
						field="expiresAt"
						rules={[{ required: true, message: "请选择过期时间" }]}
					>
						<DatePicker
							style={{ width: "100%" }}
							disabledDate={(current) => current.isBefore(Date.now())}
						/>
					</Form.Item>
				</Form>
			</Modal>
		</div>
	);
};

export default UserKeyManager;
